import Cookies from 'js-cookie';
import { NavigateFunction } from 'react-router-dom';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ApiKey, HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import {
   AdminCandidateProfileData,
   AdminCandidateProfileErrorKey,
   AdminCandidateProfileForm,
   AdminCandidateFormGender,
   AdminCandidateProfileUpdatePayload,
   AdminCandidateStatus,
   AdminCandidateCityOption,
   AdminCandidateProfileCountry,
   AdminCandidateProfileContactLink,
   AdminCandidateProfileUpdateContactLink,
   LoadAdminCandidateProfileResponse,
   LocalitySearchResponse,
   UpdateAdminCandidateProfileResponse,
} from './candidate-edit-profile.types';

const EMPTY_FORM: AdminCandidateProfileForm = {
   first_name: '',
   last_name: '',
   gender: '',
   contact: '',
   location: '',
   country_id: '',
   city_id: '',
   profile_image: null,
   email_address: '',
   new_password: '',
   confirm_password: '',
   status: 0,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const contactPattern = /^(\+\d{1,3}-?)?\d{10}$/;
type TextFieldKey =
   | 'first_name'
   | 'last_name'
   | 'contact'
   | 'email_address'
   | 'new_password'
   | 'confirm_password';
type ContactLinkKey = keyof AdminCandidateProfileContactLink;

const EMPTY_CONTACT_LINK: AdminCandidateProfileContactLink = { type: '', link: '' };
const DEFAULT_MESSENGER_OPTIONS = ['Telegram', 'WhatsApp', 'Viber', 'Skype', 'Signal', 'Discord'];
const DEFAULT_SOCIAL_OPTIONS = ['LinkedIn', 'Facebook', 'Twitter', 'Instagram', 'GitHub', 'TikTok'];

export class EditCandidateProfileStore {
   readonly loadCandidateState = new AsyncState<LoadAdminCandidateProfileResponse>();
   readonly updateCandidateState = new AsyncState<UpdateAdminCandidateProfileResponse>();

   form: AdminCandidateProfileForm = { ...EMPTY_FORM };
   errors: Partial<Record<AdminCandidateProfileErrorKey, string>> = {};
   countries: AdminCandidateProfileCountry[] = [];
   cityOptions: AdminCandidateCityOption[] = [];
   messengerRows: AdminCandidateProfileContactLink[] = [{ ...EMPTY_CONTACT_LINK }];
   socialRows: AdminCandidateProfileContactLink[] = [{ ...EMPTY_CONTACT_LINK }];
   messengerOptions: string[] = [...DEFAULT_MESSENGER_OPTIONS];
   socialOptions: string[] = [...DEFAULT_SOCIAL_OPTIONS];
   citiesLoading = false;
   temporaryProfileImage: string | null = null;
   rawCandidate: AdminCandidateProfileData | null = null;
   private citySearchRequestSeq = 0;

   constructor(
      private readonly slug: string,
      private readonly navigate: NavigateFunction
   ) {
      makeObservable<this, 'rawCandidate'>(this, {
         form: observable,
         errors: observable,
         countries: observable,
         cityOptions: observable,
         messengerRows: observable,
         socialRows: observable,
         messengerOptions: observable,
         socialOptions: observable,
         citiesLoading: observable,
         temporaryProfileImage: observable,
         rawCandidate: observable,
         isLoading: computed,
         isSaving: computed,
         avatarPreviewImage: computed,
         canAddMessengerRow: computed,
         canAddSocialRow: computed,
         maxMessengerRows: computed,
         maxSocialRows: computed,
         loadCandidate: action.bound,
         submit: action.bound,
         setField: action.bound,
         setGender: action.bound,
         setStatus: action.bound,
         updateMessengerRow: action.bound,
         addMessengerRow: action.bound,
         removeMessengerRow: action.bound,
         updateSocialRow: action.bound,
         addSocialRow: action.bound,
         removeSocialRow: action.bound,
         searchCities: action.bound,
         onCountrySelect: action.bound,
         onCitySelect: action.bound,
         clearCitySelection: action.bound,
         resetSelectedPhoto: action.bound,
      });
   }

   get isLoading(): boolean {
      return (
         this.loadCandidateState.status === 'idle' || this.loadCandidateState.status === 'pending'
      );
   }

   get isSaving(): boolean {
      return this.updateCandidateState.status === 'pending';
   }

   get avatarPreviewImage(): string | null {
      return this.temporaryProfileImage ?? this.form.profile_image;
   }

   get canAddMessengerRow(): boolean {
      return this.messengerRows.length < this.maxMessengerRows;
   }

   get canAddSocialRow(): boolean {
      return this.socialRows.length < this.maxSocialRows;
   }

   get maxMessengerRows(): number {
      return Math.max(this.messengerOptions.length, 1);
   }

   get maxSocialRows(): number {
      return Math.max(this.socialOptions.length, 1);
   }

   async loadCandidate(): Promise<void> {
      if (!this.ensureAdminSession()) {
         return;
      }

      await this.loadCandidateState.run(
         () =>
            HttpClient.post<LoadAdminCandidateProfileResponse>(
               `/admin/candidates/editusers/${this.slug}`,
               null,
               this.adminRequestConfig()
            ),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.hydrate(resp.response);
                  });
               }
            },
         }
      );
   }

   setField(field: TextFieldKey, value: string): void {
      this.form = {
         ...this.form,
         [field]: value,
      };
      this.clearError(field);
   }

   setGender(gender: AdminCandidateFormGender): void {
      this.form = {
         ...this.form,
         gender,
      };
   }

   setStatus(status: AdminCandidateStatus): void {
      this.form = {
         ...this.form,
         status,
      };
   }

   updateMessengerRow(index: number, key: ContactLinkKey, value: string): void {
      this.messengerRows = this.messengerRows.map((row, rowIndex) =>
         rowIndex === index ? { ...row, [key]: value } : row
      );
   }

   addMessengerRow(): void {
      if (!this.canAddMessengerRow) {
         return;
      }

      this.messengerRows = [...this.messengerRows, { ...EMPTY_CONTACT_LINK }];
   }

   removeMessengerRow(index: number): void {
      const nextRows = this.messengerRows.filter((_, rowIndex) => rowIndex !== index);
      this.messengerRows = nextRows.length ? nextRows : [{ ...EMPTY_CONTACT_LINK }];
   }

   updateSocialRow(index: number, key: ContactLinkKey, value: string): void {
      this.socialRows = this.socialRows.map((row, rowIndex) =>
         rowIndex === index ? { ...row, [key]: value } : row
      );
   }

   addSocialRow(): void {
      if (!this.canAddSocialRow) {
         return;
      }

      this.socialRows = [...this.socialRows, { ...EMPTY_CONTACT_LINK }];
   }

   removeSocialRow(index: number): void {
      const nextRows = this.socialRows.filter((_, rowIndex) => rowIndex !== index);
      this.socialRows = nextRows.length ? nextRows : [{ ...EMPTY_CONTACT_LINK }];
   }

   getMessengerOptions(index: number): string[] {
      return this.getAvailableOptions(this.messengerOptions, this.messengerRows, index);
   }

   getSocialOptions(index: number): string[] {
      return this.getAvailableOptions(this.socialOptions, this.socialRows, index);
   }

   getMessengerFieldLabel(type: string): string {
      if (type === 'WhatsApp' || type === 'Viber' || type === 'Signal') {
         return 'Номер телефону';
      }
      if (type === 'Skype' || type === 'Discord') {
         return "Ім'я користувача";
      }
      return "Ім'я користувача або номер телефону";
   }

   getMessengerPlaceholder(type: string): string {
      if (type === 'WhatsApp' || type === 'Viber' || type === 'Signal') {
         return '+380XXXXXXXXX';
      }
      if (type === 'Skype' || type === 'Discord') {
         return "Ім'я користувача";
      }
      return "Ім'я користувача або номер телефону";
   }

   async searchCities(query: string): Promise<void> {
      const countryId = this.form.country_id;

      if (!countryId) {
         this.citySearchRequestSeq += 1;
         runInAction(() => {
            this.cityOptions = [];
            this.citiesLoading = false;
         });
         return;
      }

      const requestSeq = ++this.citySearchRequestSeq;
      runInAction(() => {
         this.citiesLoading = true;
      });

      try {
         const resp = await HttpClient.post<LocalitySearchResponse>(
            '/locality/search',
            {
               country_id: countryId,
               query,
            },
            this.adminRequestConfig()
         );

         if (resp.status === 200 && requestSeq === this.citySearchRequestSeq) {
            runInAction(() => {
               this.cityOptions = resp.response.map((city) => ({
                  id: city.id,
                  name: city.name,
               }));
            });
         }
      } finally {
         if (requestSeq === this.citySearchRequestSeq) {
            runInAction(() => {
               this.citiesLoading = false;
            });
         }
      }
   }

   onCountrySelect(countryId: number): void {
      this.form = {
         ...this.form,
         country_id: countryId,
         city_id: '',
      };
      this.cityOptions = [];
      this.clearError('country_id');
      this.clearError('city_id');
   }

   onCitySelect(cityId: number): void {
      this.form = {
         ...this.form,
         city_id: cityId,
         location: this.cityOptions.find((city) => city.id === cityId)?.name ?? this.form.location,
      };
      this.clearError('city_id');
   }

   clearCitySelection(): void {
      this.form = {
         ...this.form,
         city_id: '',
      };
      this.clearError('city_id');
   }

   async selectPhotoFile(file: File): Promise<{ error?: string }> {
      try {
         const base64 = await this.convertToBase64(file);
         const temporaryImage = URL.createObjectURL(file);

         runInAction(() => {
            this.resetSelectedPhoto();
            this.temporaryProfileImage = temporaryImage;
            this.form = {
               ...this.form,
               profile_image: base64,
            };
         });

         return {};
      } catch {
         return { error: 'Invalid image file' };
      }
   }

   resetSelectedPhoto(): void {
      if (this.temporaryProfileImage?.startsWith('blob:')) {
         URL.revokeObjectURL(this.temporaryProfileImage);
      }
      this.temporaryProfileImage = null;
      this.form = {
         ...this.form,
         profile_image: this.rawCandidate?.profile_image ?? null,
      };
   }

   validate(): boolean {
      const nextErrors: Partial<Record<AdminCandidateProfileErrorKey, string>> = {};
      const form = this.form;

      if (!form.first_name.trim()) {
         nextErrors.first_name = 'First Name is required';
      }
      if (!form.last_name.trim()) {
         nextErrors.last_name = 'Last Name is required';
      }
      if (!form.contact.trim()) {
         nextErrors.contact = 'Contact Number is required';
      }
      if (!form.country_id) {
         nextErrors.country_id = 'Country is required';
      }
      if (!form.city_id) {
         nextErrors.city_id = 'City is required';
      }
      if (!form.email_address.trim()) {
         nextErrors.email_address = 'Email is required';
      }

      if (form.new_password || form.confirm_password) {
         if (!form.new_password) {
            nextErrors.new_password = 'New Password is required';
         }
         if (!form.confirm_password) {
            nextErrors.confirm_password = 'Confirm password is required';
         }
         if (
            form.new_password &&
            form.confirm_password &&
            form.new_password !== form.confirm_password
         ) {
            nextErrors.confirm_password = 'New Password and Confirm Password do not match';
         }
         if (form.new_password && form.new_password.length < 8) {
            nextErrors.new_password = 'Please enter atleast 8 characters';
         }
         if (form.confirm_password && form.confirm_password.length < 8) {
            nextErrors.confirm_password = 'Please enter atleast 8 characters';
         }
      }

      if (form.email_address && !emailPattern.test(form.email_address)) {
         nextErrors.email_address = 'Invalid Email Address';
      }

      if (form.contact && !contactPattern.test(form.contact)) {
         nextErrors.contact = 'Please enter contact number under 10 digits';
      }

      this.errors = nextErrors;
      return Object.keys(nextErrors).length === 0;
   }

   async submit({
      onSuccess,
      onError,
   }: {
      onSuccess?: () => void;
      onError?: (message: string) => void;
   } = {}): Promise<void> {
      if (!this.validate()) {
         return;
      }

      if (!this.ensureAdminSession()) {
         return;
      }

      await this.updateCandidateState.run(
         () =>
            HttpClient.post<UpdateAdminCandidateProfileResponse>(
               `/admin/candidates/editusers/${this.slug}`,
               this.buildPayload(),
               this.adminRequestConfig()
            ),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  onSuccess?.();
                  this.navigate('/admin/candidates');
               } else {
                  onError?.(resp.message || 'Could not update jobseeker');
               }
            },
            onError: (error) => {
               onError?.(error.message || 'Could not update jobseeker. Please try again later!');
            },
         }
      );
   }

   private hydrate(candidate: AdminCandidateProfileData): void {
      this.rawCandidate = candidate;
      this.form = {
         first_name: this.toInputValue(candidate.first_name),
         last_name: this.toInputValue(candidate.last_name),
         gender: this.toGenderValue(candidate.gender),
         contact: this.toInputValue(candidate.contact),
         location: this.toInputValue(candidate.location),
         country_id: this.toNumberInputValue(candidate.country_id),
         city_id: this.toNumberInputValue(candidate.city_id),
         profile_image: candidate.profile_image ?? null,
         email_address: this.toInputValue(candidate.email_address),
         new_password: '',
         confirm_password: '',
         status: this.toStatus(candidate.status),
      };
      this.errors = {};
      this.countries = this.normalizeCountries(candidate);
      this.cityOptions = this.normalizeCityOptions(candidate);
      this.messengerOptions = candidate.messengers?.length
         ? [...candidate.messengers]
         : [...DEFAULT_MESSENGER_OPTIONS];
      this.socialOptions = candidate.socials?.length
         ? [...candidate.socials]
         : [...DEFAULT_SOCIAL_OPTIONS];
      this.messengerRows = this.ensureLinkRows(candidate.messenger_links);
      this.socialRows = this.ensureLinkRows(candidate.social_links);
      this.enforceLinkRowsLimit();
      this.temporaryProfileImage = null;
   }

   private buildPayload(): AdminCandidateProfileUpdatePayload {
      return {
         ...(this.rawCandidate ?? EMPTY_FORM),
         ...this.form,
         first_name: this.form.first_name.trim(),
         last_name: this.form.last_name.trim(),
         gender: this.toApiGender(this.form.gender),
         contact: this.form.contact.trim(),
         location: this.getSelectedCityName() ?? this.form.location.trim(),
         country_id: this.nullableNumber(this.form.country_id),
         city_id: this.nullableNumber(this.form.city_id),
         email_address: this.form.email_address.trim(),
         social_links: this.mapLinks(this.socialRows),
         messenger_links: this.mapLinks(this.messengerRows),
      };
   }

   private adminRequestConfig() {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      return {
         headers: {
            key: ApiKey,
            adminid: adminID,
            ContentType: 'application/json',
            Authorization: `Bearer ${tokenKey}`,
         },
      };
   }

   private ensureAdminSession(): boolean {
      const tokenKey = Cookies.get('token');
      if (!tokenKey) {
         this.navigate('/admin');
         return false;
      }
      return true;
   }

   private clearError(field: AdminCandidateProfileErrorKey): void {
      if (this.errors[field]) {
         this.errors = {
            ...this.errors,
            [field]: '',
         };
      }
   }

   private toInputValue(value: unknown): string {
      return typeof value === 'string' ? value : '';
   }

   private toNumberInputValue(value: unknown): number | '' {
      return typeof value === 'number' ? value : '';
   }

   private toStatus(value: unknown): AdminCandidateStatus {
      return Number(value) === 1 ? 1 : 0;
   }

   private toGenderValue(gender: string | null): AdminCandidateFormGender {
      const normalized = (gender ?? '').toLowerCase().trim();
      if (
         normalized === 'male' ||
         normalized === 'man' ||
         normalized === '\u0447\u043e\u043b\u043e\u0432\u0456\u043a'
      ) {
         return 'male';
      }
      if (
         normalized === 'female' ||
         normalized === 'woman' ||
         normalized === '\u0436\u0456\u043d\u043a\u0430'
      ) {
         return 'female';
      }
      return '';
   }

   private toApiGender(gender: AdminCandidateFormGender): 'woman' | 'man' | null {
      if (gender === 'male') {
         return 'man';
      }
      if (gender === 'female') {
         return 'woman';
      }
      return null;
   }

   private normalizeCountries(
      candidate: AdminCandidateProfileData
   ): AdminCandidateProfileCountry[] {
      const countries = candidate.countries ?? [];
      const countryId = candidate.country_id;
      const countryName = this.getCountryName(candidate);

      if (!countryId || !countryName || countries.some((country) => country.id === countryId)) {
         return countries;
      }

      return [
         {
            id: countryId,
            name: countryName,
         },
         ...countries,
      ];
   }

   private normalizeCityOptions(candidate: AdminCandidateProfileData): AdminCandidateCityOption[] {
      const cityId = candidate.city_id ?? candidate.city?.id ?? candidate.city?.value;
      const cityName = this.getCityName(candidate);

      if (!cityId || !cityName) {
         return [];
      }

      return [
         {
            id: cityId,
            name: cityName,
         },
      ];
   }

   private getCountryName(candidate: AdminCandidateProfileData): string {
      return (
         this.toInputValue(candidate.country_name) ||
         this.toInputValue(candidate.city?.country_name)
      );
   }

   private getCityName(candidate: AdminCandidateProfileData): string {
      return (
         this.toInputValue(candidate.city?.label) || this.toInputValue(candidate.city?.city_name)
      );
   }

   private nullableNumber(value: number | ''): number | null {
      return value === '' ? null : value;
   }

   private nullableText(value: string): string | null {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
   }

   private mapLinks(
      rows: AdminCandidateProfileContactLink[]
   ): AdminCandidateProfileUpdateContactLink[] | null {
      const links = rows
         .map((row) => ({
            link: this.nullableText(row.link),
            type: this.nullableText(row.type),
         }))
         .filter((row) => row.link !== null || row.type !== null);

      return links.length ? links : null;
   }

   private ensureLinkRows(
      rows: AdminCandidateProfileContactLink[] | null | undefined
   ): AdminCandidateProfileContactLink[] {
      if (!rows || rows.length === 0) {
         return [{ ...EMPTY_CONTACT_LINK }];
      }

      return rows.map((row) => ({
         type: row.type ?? '',
         link: row.link ?? '',
      }));
   }

   private enforceLinkRowsLimit(): void {
      if (this.messengerRows.length > this.maxMessengerRows) {
         this.messengerRows = this.messengerRows.slice(0, this.maxMessengerRows);
      }
      if (this.socialRows.length > this.maxSocialRows) {
         this.socialRows = this.socialRows.slice(0, this.maxSocialRows);
      }
   }

   private getAvailableOptions(
      options: string[],
      rows: AdminCandidateProfileContactLink[],
      currentIndex: number
   ): string[] {
      const currentValue = rows[currentIndex]?.type ?? '';
      const optionsPool = this.withCurrentOption(options, currentValue);

      const selectedValues = rows
         .map((row, index) => (index === currentIndex ? '' : row.type))
         .filter(Boolean);

      return optionsPool.filter((option) => !selectedValues.includes(option));
   }

   private withCurrentOption(options: string[], currentValue: string): string[] {
      if (!currentValue || options.includes(currentValue)) {
         return options;
      }
      return [...options, currentValue];
   }

   private getSelectedCityName(): string | null {
      if (!this.form.city_id) {
         return null;
      }

      return this.cityOptions.find((city) => city.id === this.form.city_id)?.name ?? null;
   }

   private convertToBase64(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
         const fileReader = new FileReader();
         fileReader.readAsDataURL(file);
         fileReader.onload = () => {
            resolve(fileReader.result as string);
         };
         fileReader.onerror = (error) => {
            reject(error);
         };
      });
   }
}
